import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { api, ApiError } from '@/lib/api';
import {
  reasonToSend,
  requestSource,
  resolutionPrompt,
  statusChangeFailure,
  type StatusChange,
} from '@/lib/change-request-status';

export type ChangeRequestType = {
  id: string;
  label: string;
  help?: string;
  fields: string[];
  example?: string;
  needsArtwork?: boolean;
};

export type ChangeRequest = {
  id: string;
  type: string;
  status: 'open' | 'in_progress' | 'done' | 'declined' | 'applied';
  notes?: string;
  details?: Record<string, string>;
  requestedBy?: string;
  requestedAt?: string;
  resolutionNote?: string;
  requestFingerprint?: string;
  proofDeliveredAt?: string;
  // Customer uploads from the hosted proof page (#910). Addressed by index —
  // the API never hands the client a bucket path (lib/change-request-attachments.js).
  files?: ChangeRequestFile[];
};

export type ChangeRequestFile = {
  index: number;
  filename: string;
  mimeType: string;
  byteLength: number | null;
  previewable: boolean;
};

export function formatBytes(n: number | null): string {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export const REQUEST_STATUS_TONE: Record<string, string> = {
  open: 'bg-warning/10 text-warning',
  in_progress: 'bg-primary/10 text-primary',
  done: 'bg-bg text-muted',
  declined: 'bg-bg text-muted',
  applied: 'bg-success/10 text-success',
};

export const FIELD_LABELS: Record<string, string> = {
  startDate: 'Open date',
  endDate: 'Close date',
  styleNumber: 'Style',
  size: 'Size',
  text: 'Text',
  sport: 'Sport',
  notes: 'Notes',
};

// The request list + Start/Done/Decline, shared by the store page's Requests
// tab and the Revisions drawer. Done asks what was changed; that note is the
// record of the response (resolutionNote).
export function ChangeRequestList({
  storeId,
  requests,
  types,
  onChanged,
}: {
  storeId: string;
  requests: ChangeRequest[];
  types: ChangeRequestType[];
  onChanged: () => Promise<unknown>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function setStatus(id: string, status: StatusChange) {
    const question = resolutionPrompt(status);
    const decision = reasonToSend(status, question ? window.prompt(question) : null);
    if (!decision.send) {
      if (decision.message) setError(decision.message);
      return;
    }
    setBusy(true);
    setError('');
    try {
      await api(`/api/team-stores/${encodeURIComponent(storeId)}/requests/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'fetch' },
        body: JSON.stringify({ status, reason: decision.reason }),
      });
      // A failed refresh must not turn a saved change into an error message;
      // the list revalidates on next focus anyway.
      await onChanged().catch(() => {});
    } catch (e) {
      const failure = statusChangeFailure(e instanceof ApiError ? e.status : null, e instanceof ApiError ? e.body : null);
      setError(failure.message);
      if (failure.refresh) await onChanged().catch(() => {});
    } finally {
      setBusy(false);
    }
  }

  if (requests.length === 0) {
    return <p className="text-[12px] text-muted">Nothing requested for this store yet.</p>;
  }

  return (
    <>
      {error ? <p role="status" aria-live="polite" className="mb-2 text-[12px] text-danger">{error}</p> : null}
      <ul className="space-y-2">
        {requests.map((r) => {
          const t = types.find((x) => x.id === r.type);
          const status = r.status || 'open';
          const live = status === 'open' || status === 'in_progress';
          return (
            <li key={r.id} className="rounded-lg border border-black/[0.06] bg-bg p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[13px] font-medium">{t?.label || r.type}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.04em] ${REQUEST_STATUS_TONE[status] || 'bg-bg text-muted'}`}>
                  {status.replace('_', ' ')}
                </span>
                <span className="ml-auto text-[11px] text-muted">
                  {requestSource(r)}{r.requestedAt ? ` · ${new Date(r.requestedAt).toLocaleDateString()}` : ''}
                </span>
              </div>
              {r.details && Object.keys(r.details).length > 0 ? (
                <p className="mt-1 text-[12px] text-muted">
                  {Object.entries(r.details).map(([k, v]) => `${FIELD_LABELS[k] || k}: ${v}`).join(' · ')}
                </p>
              ) : null}
              {r.notes ? <p className="mt-1 whitespace-pre-wrap break-words text-[12.5px]">{r.notes}</p> : null}
              {r.files && r.files.length > 0 ? (
                <ul className="mt-2 flex flex-wrap gap-2" aria-label="Files the customer uploaded">
                  {r.files.map((f) => {
                    const href = `/api/team-stores/${encodeURIComponent(storeId)}/requests/${encodeURIComponent(r.id)}/attachments/${f.index}`;
                    return (
                      <li key={f.index}>
                        <a
                          href={href}
                          download={f.filename}
                          className="flex items-center gap-2 rounded-lg border border-black/[0.08] bg-surface px-2 py-1.5 text-[12px] hover:border-primary/40"
                        >
                          {f.previewable ? (
                            <img src={href} alt="" loading="lazy" className="h-10 w-10 rounded bg-white object-contain" />
                          ) : (
                            <span className="flex h-10 w-10 items-center justify-center rounded bg-bg text-[10px] font-semibold uppercase text-muted">PDF</span>
                          )}
                          <span className="min-w-0">
                            <span className="block break-all font-medium">{f.filename}</span>
                            <span className="block text-[11px] text-muted">
                              Customer upload{f.byteLength != null ? ` · ${formatBytes(f.byteLength)}` : ''} · Download
                            </span>
                          </span>
                        </a>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
              {r.resolutionNote ? <p className="mt-1 text-[12px] text-muted">Resolution: {r.resolutionNote}</p> : null}
              {status === 'applied' && r.proofDeliveredAt ? (
                <p className="mt-1 text-[12px] text-muted">
                  Answered by the proof emailed {new Date(r.proofDeliveredAt).toLocaleDateString()}
                </p>
              ) : null}
              {live ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {status === 'open' ? (
                    <Button size="sm" variant="secondary" disabled={busy} onClick={() => setStatus(r.id, 'in_progress')}>Start</Button>
                  ) : null}
                  <Button size="sm" variant="secondary" disabled={busy} onClick={() => setStatus(r.id, 'done')}>Done</Button>
                  <Button size="sm" variant="secondary" disabled={busy} onClick={() => setStatus(r.id, 'declined')}>Decline</Button>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </>
  );
}
